import { Html } from '@react-three/drei';
import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import type { MeasurementSceneGuide } from './viewerMeasurements';

const TICK_HALF_LENGTH = 0.06;

interface MeasurementSceneGuideLineProps {
  guide: MeasurementSceneGuide;
}

function buildTickPoints(guide: MeasurementSceneGuide): THREE.Vector3[] {
  const axis = guide.tickAxis;
  if (!axis) return [];
  const [sx, sy, sz] = guide.start;
  const [ex, ey, ez] = guide.end;
  const offset = TICK_HALF_LENGTH;
  if (axis === 'y') {
    return [
      new THREE.Vector3(sx, sy - offset, sz),
      new THREE.Vector3(sx, sy + offset, sz),
      new THREE.Vector3(ex, ey - offset, ez),
      new THREE.Vector3(ex, ey + offset, ez),
    ];
  }
  if (axis === 'x') {
    return [
      new THREE.Vector3(sx - offset, sy, sz),
      new THREE.Vector3(sx + offset, sy, sz),
      new THREE.Vector3(ex - offset, ey, ez),
      new THREE.Vector3(ex + offset, ey, ez),
    ];
  }
  return [
    new THREE.Vector3(sx, sy, sz - offset),
    new THREE.Vector3(sx, sy, sz + offset),
    new THREE.Vector3(ex, ey, ez - offset),
    new THREE.Vector3(ex, ey, ez + offset),
  ];
}

function MeasurementSceneGuideLine({ guide }: MeasurementSceneGuideLineProps) {
  const line = useMemo(() => {
    const geometry = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(...guide.start),
      new THREE.Vector3(...guide.end),
    ]);
    const material = new THREE.LineDashedMaterial({
      color: '#f6f0d8',
      dashSize: guide.emphasize ? 0.18 : 0.12,
      gapSize: guide.emphasize ? 0.06 : 0.08,
      transparent: true,
      opacity: guide.emphasize ? 1 : 0.95,
      depthTest: false,
      linewidth: 1,
    });
    const nextLine = new THREE.Line(geometry, material);
    nextLine.computeLineDistances();
    nextLine.renderOrder = 20;
    return nextLine;
  }, [guide.emphasize, guide.end, guide.start]);

  const ticks = useMemo(() => {
    const points = buildTickPoints(guide);
    if (points.length === 0) return null;
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color: '#f6f0d8',
      transparent: true,
      opacity: guide.emphasize ? 1 : 0.95,
      depthTest: false,
    });
    const segments = new THREE.LineSegments(geometry, material);
    segments.renderOrder = 21;
    return segments;
  }, [guide]);

  useEffect(() => () => {
    line.geometry.dispose();
    line.material.dispose();
    if (ticks) {
      ticks.geometry.dispose();
      (ticks.material as THREE.Material).dispose();
    }
  }, [line, ticks]);

  return (
    <>
      <primitive object={line} />
      {ticks ? <primitive object={ticks} /> : null}
      <Html position={guide.labelPosition} center transform={false} pointerEvents="none">
        <div className={`viewer-measure-badge${guide.emphasize ? ' viewer-measure-badge--emphasized' : ''}`}>
          {guide.label}
        </div>
      </Html>
    </>
  );
}

interface MeasurementSceneGuidesProps {
  guides: MeasurementSceneGuide[];
}

export function MeasurementSceneGuides({ guides }: MeasurementSceneGuidesProps) {
  return (
    <>
      {guides.map((guide) => (
        <MeasurementSceneGuideLine key={guide.id} guide={guide} />
      ))}
    </>
  );
}
